import React, { useState } from "react";
import { toast } from "react-hot-toast";
import {
  getFirestore,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { FileText, CheckCircle, Upload } from "lucide-react";

const JDUpload: React.FC = () => {
  const { user } = useAuth();

  type Step = "input" | "preview" | "complete";
  const [step, setStep] = useState<Step>("input");
  const [jobDescription, setJobDescription] = useState<string>("");

  // New state for additional fields
  const [jobTitle, setJobTitle] = useState("");
  const [education, setEducation] = useState("");
  const [experience, setExperience] = useState("");
  const [jobPosition, setJobPosition] = useState("");
  const [showSalary, setShowSalary] = useState(false);
  const [salary, setSalary] = useState("");

  const [saving, setSaving] = useState(false);

  const handleProcess = () => {
    if (!jobDescription.trim()) return;
    setStep("preview");
  };

  const handlePublish = async () => {
    if (!user) {
      toast.error("Please sign in again");
      return;
    }
    if (!jobTitle || !education || !experience || !jobPosition) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (showSalary && !salary) {
      toast.error("Please enter a minimum salary.");
      return;
    }

    try {
      setSaving(true);
      const db = getFirestore(firebaseApp);

      const jobData: any = {
        hrId: user.id,
        hrEmail: user.email,
        jobTitle,
        jobDescription,
        education,
        experience: parseInt(experience, 10),
        jobPosition,
        createdAt: serverTimestamp(),
      };

      if (showSalary && salary) {
        jobData.salary = parseInt(salary, 10);
      }

      await addDoc(collection(db, "hrJobs"), jobData);
      toast.success("Job description published successfully!");
      setStep("complete");
    } catch (err) {
      toast.error("Failed to publish job. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (step === "complete") {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center space-y-6">
        <h1 className="text-4xl font-bold text-gray-900">Job Published!</h1>
        <p className="text-base text-gray-600">
          You can now generate tests or upload another JD.
        </p>
        <button
          onClick={() => {
            setJobDescription("");
            setJobTitle("");
            setEducation("");
            setExperience("");
            setJobPosition("");
            setShowSalary(false);
            setSalary("");
            setStep("input");
          }}
          className="btn-secondary"
        >
          Upload Another JD
        </button>
      </div>
    );
  }

  if (step === "preview") {
    const isFormValid = jobTitle && education && experience && jobPosition;

    return (
      <div className="max-w-3xl mx-auto space-y-6 py-10">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Review Job Description
        </h1>
        <textarea
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          className="input-glass w-full h-80 resize-none"
        />

        {/* Additional Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title
            </label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g., Senior Frontend Developer"
              className="input-glass w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Education
            </label>
            <select
              value={education}
              onChange={(e) => setEducation(e.target.value)}
              className="input-glass w-full"
            >
              <option value="" disabled>
                Select...
              </option>
              <option value="3-year-degree">3-Year Degree</option>
              <option value="4-year-degree">4-Year Degree</option>
              <option value="masters">Masters</option>
              <option value="phd">PhD</option>
              <option value="none">None</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Years of Experience
            </label>
            <input
              type="number"
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              placeholder="e.g., 5"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Position
            </label>
            <select
              value={jobPosition}
              onChange={(e) => setJobPosition(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="" disabled>
                Select...
              </option>
              <option value="internship">Internship</option>
              <option value="part-time">Part-time</option>
              <option value="full-time">Full-time</option>
              <option value="contract">Contract</option>
            </select>
          </div>
          {/* Salary Field */}
          <div>
            <div className="flex items-center mb-2">
              <input
                id="show-salary"
                type="checkbox"
                checked={showSalary}
                onChange={(e) => setShowSalary(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label
                htmlFor="show-salary"
                className="ml-2 block text-sm font-medium text-gray-900"
              >
                Mention minimum salary
              </label>
            </div>
            {showSalary && (
              <input
                type="number"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                placeholder="e.g., 80000 (per annum)"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            )}
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <button
            onClick={() => setStep("input")}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button
            onClick={handlePublish}
            disabled={saving || !isFormValid}
            className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50"
          >
            {saving ? "Publishing..." : "Publish Job"}
          </button>
        </div>
      </div>
    );
  }

  // Input step
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Upload Job Description
          </h1>
          <p className="text-lg text-gray-600">
            Upload your job description and let our AI extract key requirements
            and generate tests
          </p>
        </div>

        <div className="space-y-4">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Paste Job Description Text
            </h3>
            <p className="text-gray-600">
              Copy and paste your job description directly
            </p>
          </div>

          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste your complete job description here including job title, requirements, responsibilities, benefits, etc..."
            className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 resize-none"
          />

          <div className="text-center">
            <button
              onClick={handleProcess}
              disabled={!jobDescription.trim()}
              className="inline-flex items-center px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-5 h-5 mr-2" />
              Process Job Description
            </button>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Smart Analysis</h3>
          <p className="text-sm text-gray-600">
            AI extracts skills, requirements, and experience levels
            automatically
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">
            Auto Test Generation
          </h3>
          <p className="text-sm text-gray-600">
            Automatically generate skill-based tests from job requirements
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Upload className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Instant Matching</h3>
          <p className="text-sm text-gray-600">
            Match with qualified candidates immediately after publishing
          </p>
        </div>
      </div>
    </div>
  );
};

export default JDUpload;
